import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'

import { geologyAjax } from '../ajax/GeologyAjax'
import { store } from '../state/store'
import { queryClient } from '../queryClient'
import { GeoRehandleDTO } from '../DTOs/Geology/GeoRehandleDTO'
import { PagedResult } from '../DTOs/Common/PagedResult'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import { nextTempId } from './tempId'
import { TripleShift } from '../types/Shift'
import { isOnlineNow } from './connectivity'
import { mayQueueOffline, offlineRefusal } from './offlineWrites'
import { sendDirect } from './directSave'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

function provisionalPlantId(operationId: number): number | undefined {
  if (operationId >= 0) return undefined

  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs'],
  })) {
    const match = (page?.data ?? []).find(row => row?.operationId === operationId)
    if (match?.plantId) return match.plantId
  }
  return undefined
}

export const geoRehandleMutations = {
  async addGeoRehandle(dto: GeoRehandleDTO, date: DateTime, shift: TripleShift) {
    const clientId = uuidv4()
    const sentDirect = isOnlineNow()
    if (sentDirect) {
      const sent = await sendDirect(() => geologyAjax.addGeoRehandle(dto, date, shift, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    const tempId = uuidv4()
    const tempGeoRehandleId = await nextTempId()
    await enqueue({
      clientId,
      sentDirect,
      entity: 'geoRehandle',
      operation: 'create',
      shiftId: currentShiftId(),
      tempId,
      entityId: tempGeoRehandleId,
      data: { ...dto, operationPlantId: provisionalPlantId(dto.operationId) },
    })
    return {
      success: true,
      data: {
        rehandle: new GeoRehandleDTO({ ...dto, geoRehandleId: tempGeoRehandleId }),
        totalLoads: 0,
        totalTonnes: 0,
      },
    }
  },

  async updateGeoRehandle(dto: GeoRehandleDTO) {
    const clientId = uuidv4()
    if (isOnlineNow()) {
      const sent = await sendDirect(() => geologyAjax.updateGeoRehandle(dto, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    await enqueue({
      clientId,
      entity: 'geoRehandle',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: dto.geoRehandleId,
      rowVersion: dto.rowVersion,
      data: { ...dto },
    })
    return {
      success: true,
      data: {
        rehandle: dto,
        totalLoads: 0,
        totalTonnes: 0,
      },
    }
  },

  async deleteGeoRehandle(geoRehandleId: number) {
    const clientId = uuidv4()
    if (isOnlineNow()) {
      const sent = await sendDirect(() => geologyAjax.deleteGeoRehandle(geoRehandleId, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    await enqueue({
      clientId,
      entity: 'geoRehandle',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: geoRehandleId,
    })
    return {
      success: true,
      data: {
        rehandle: null,
        totalLoads: 0,
        totalTonnes: 0,
      },
    }
  },
}
